import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertCircle, CheckCircle, XCircle, Info, Sparkles, Menu, X, ChevronDown, FileText, Shield, Zap } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import Footer from '../components/Footer';
import LanguageSwitcher from '../components/LanguageSwitcher';

const PlatformRequirements = () => {
  const { t } = useTranslation();
  const [expandedPlatform, setExpandedPlatform] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const platforms = [
    {
      id: 'instagram',
      name: 'Instagram',
      logo: '/logos/instagram.png',
      color: 'from-pink-500 to-purple-600',
      requirements: [
        { text: t('platformRequirements.platforms.instagram.requirements.businessAccount'), required: true },
        { text: t('platformRequirements.platforms.instagram.requirements.facebookPage'), required: true },
        { text: t('platformRequirements.platforms.instagram.requirements.adminRights'), required: true },
        { text: t('platformRequirements.platforms.instagram.requirements.personalNotSupported'), required: true, warning: true }
      ],
      limits: t('platformRequirements.platforms.instagram.limits', { returnObjects: true }),
      steps: t('platformRequirements.platforms.instagram.steps', { returnObjects: true }),
      warnings: t('platformRequirements.platforms.instagram.warnings', { returnObjects: true }),
      apiInfo: t('platformRequirements.platforms.instagram.apiInfo')
    },
    {
      id: 'facebook',
      name: 'Facebook',
      logo: '/logos/facebook.png',
      color: 'from-blue-500 to-blue-700',
      requirements: [
        { text: t('platformRequirements.platforms.facebook.requirements.hasPage'), required: true },
        { text: t('platformRequirements.platforms.facebook.requirements.pageAdmin'), required: true },
        { text: t('platformRequirements.platforms.facebook.requirements.verified'), required: true }
      ],
      limits: t('platformRequirements.platforms.facebook.limits', { returnObjects: true }),
      steps: t('platformRequirements.platforms.facebook.steps', { returnObjects: true }),
      warnings: t('platformRequirements.platforms.facebook.warnings', { returnObjects: true }),
      apiInfo: t('platformRequirements.platforms.facebook.apiInfo')
    },
    {
      id: 'twitter',
      name: 'Twitter (X)',
      logo: '/logos/twitter.png',
      color: 'from-blue-400 to-blue-600',
      requirements: [
        { text: t('platformRequirements.platforms.twitter.requirements.active'), required: true },
        { text: t('platformRequirements.platforms.twitter.requirements.emailVerified'), required: true },
        { text: t('platformRequirements.platforms.twitter.requirements.notSuspended'), required: true },
        { text: t('platformRequirements.platforms.twitter.requirements.blueOptional'), required: false }
      ],
      limits: t('platformRequirements.platforms.twitter.limits', { returnObjects: true }),
      steps: t('platformRequirements.platforms.twitter.steps', { returnObjects: true }),
      warnings: t('platformRequirements.platforms.twitter.warnings', { returnObjects: true }),
      apiInfo: t('platformRequirements.platforms.twitter.apiInfo'),
      limitedQuota: true
    },
    {
      id: 'linkedin',
      name: 'LinkedIn',
      logo: '/logos/linkedin.png',
      color: 'from-blue-600 to-blue-800',
      requirements: [
        { text: t('platformRequirements.platforms.linkedin.requirements.active'), required: true },
        { text: t('platformRequirements.platforms.linkedin.requirements.profileOrPage'), required: false },
        { text: t('platformRequirements.platforms.linkedin.requirements.emailVerified'), required: true },
        { text: t('platformRequirements.platforms.linkedin.requirements.pageAdmin'), required: true }
      ],
      limits: t('platformRequirements.platforms.linkedin.limits', { returnObjects: true }),
      steps: t('platformRequirements.platforms.linkedin.steps', { returnObjects: true }),
      warnings: t('platformRequirements.platforms.linkedin.warnings', { returnObjects: true }),
      apiInfo: t('platformRequirements.platforms.linkedin.apiInfo')
    },
    {
      id: 'tiktok',
      name: 'TikTok',
      logo: '/logos/tiktok.png',
      color: 'from-black to-pink-600',
      requirements: [
        { text: t('platformRequirements.platforms.tiktok.requirements.active'), required: true },
        { text: t('platformRequirements.platforms.tiktok.requirements.businessOptional'), required: false },
        { text: t('platformRequirements.platforms.tiktok.requirements.ageLimit'), required: true },
        { text: t('platformRequirements.platforms.tiktok.requirements.verified'), required: true }
      ],
      limits: t('platformRequirements.platforms.tiktok.limits', { returnObjects: true }),
      steps: t('platformRequirements.platforms.tiktok.steps', { returnObjects: true }),
      warnings: t('platformRequirements.platforms.tiktok.warnings', { returnObjects: true }),
      apiInfo: t('platformRequirements.platforms.tiktok.apiInfo'),
      beta: true
    },
    {
      id: 'youtube',
      name: 'YouTube',
      logo: '/logos/youtube.png',
      color: 'from-red-500 to-red-700',
      requirements: [
        { text: t('platformRequirements.platforms.youtube.requirements.channel'), required: true },
        { text: t('platformRequirements.platforms.youtube.requirements.goodStanding'), required: true },
        { text: t('platformRequirements.platforms.youtube.requirements.emailVerified'), required: true },
        { text: t('platformRequirements.platforms.youtube.requirements.partnerOptional'), required: false }
      ],
      limits: t('platformRequirements.platforms.youtube.limits', { returnObjects: true }),
      steps: t('platformRequirements.platforms.youtube.steps', { returnObjects: true }),
      warnings: t('platformRequirements.platforms.youtube.warnings', { returnObjects: true }),
      apiInfo: t('platformRequirements.platforms.youtube.apiInfo')
    },
    {
      id: 'pinterest',
      name: 'Pinterest',
      logo: '/logos/pinterest.png',
      color: 'from-red-600 to-red-800',
      requirements: [
        { text: t('platformRequirements.platforms.pinterest.requirements.active'), required: true },
        { text: t('platformRequirements.platforms.pinterest.requirements.businessOptional'), required: false },
        { text: t('platformRequirements.platforms.pinterest.requirements.hasBoard'), required: true },
        { text: t('platformRequirements.platforms.pinterest.requirements.emailVerified'), required: true }
      ],
      limits: t('platformRequirements.platforms.pinterest.limits', { returnObjects: true }),
      steps: t('platformRequirements.platforms.pinterest.steps', { returnObjects: true }),
      warnings: t('platformRequirements.platforms.pinterest.warnings', { returnObjects: true }),
      apiInfo: t('platformRequirements.platforms.pinterest.apiInfo')
    }
  ];

  const togglePlatform = (platformId) => {
    setExpandedPlatform(expandedPlatform === platformId ? null : platformId);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 bg-white/80 backdrop-blur-lg border-b border-gray-200 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2">
              <img
                src="/logo.svg"
                alt="Social Octo Logo"
                className="w-10 h-10"
              />
              <span className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                Social Octo
              </span>
            </Link>

            {/* Desktop Menu */}
            <div className="hidden md:flex items-center gap-8">
              <Link to="/#features" className="text-gray-700 hover:text-blue-600 font-medium transition">
                {t('nav.features')}
              </Link>
              <Link to="/#pricing" className="text-gray-700 hover:text-blue-600 font-medium transition">
                {t('nav.pricing')}
              </Link>

              {/* Dokümanlar Dropdown */}
              <div className="relative group">
                <button className="flex items-center gap-1 text-gray-700 hover:text-blue-600 font-medium transition py-2">
                  <FileText className="w-4 h-4" />
                  {t('howItWorks.nav.docs')}
                  <ChevronDown className="w-4 h-4 transition-transform group-hover:rotate-180" />
                </button>

                {/* Dropdown Menu - görünmez köprü ile */}
                <div className="absolute top-full left-0 pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                  <div className="w-64 bg-white rounded-lg shadow-xl border border-gray-200 py-2">
                    <Link
                      to="/how-it-works"
                      className="flex items-center gap-3 px-4 py-3 text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition"
                    >
                      <Zap className="w-5 h-5" />
                      <div>
                        <div className="font-medium">{t('howItWorks.nav.howItWorks.title')}</div>
                        <div className="text-xs text-gray-500">{t('howItWorks.nav.howItWorks.subtitle')}</div>
                      </div>
                    </Link>
                    <Link
                      to="/platform-requirements"
                      className="flex items-center gap-3 px-4 py-3 text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition"
                    >
                      <Shield className="w-5 h-5" />
                      <div>
                        <div className="font-medium">{t('howItWorks.nav.platformRequirements.title')}</div>
                        <div className="text-xs text-gray-500">{t('howItWorks.nav.platformRequirements.subtitle')}</div>
                      </div>
                    </Link>
                  </div>
                </div>
              </div>

              <LanguageSwitcher />

              <Link to="/login" className="bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 text-gray-700 hover:from-blue-600 hover:to-purple-600 hover:border-transparent hover:text-white font-medium transition px-5 py-1.5 rounded-lg">
                {t('nav.login')}
              </Link>
              <Link
                to="/register"
                className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-2 rounded-lg font-semibold hover:shadow-lg transition"
              >
                {t('nav.register')}
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 rounded-lg hover:bg-gray-100"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden py-4 border-t border-gray-200">
              <div className="flex flex-col gap-4">
                <Link to="/#features" className="text-gray-700 hover:text-blue-600 font-medium transition">
                  {t('nav.features')}
                </Link>
                <Link to="/#pricing" className="text-gray-700 hover:text-blue-600 font-medium transition">
                  {t('nav.pricing')}
                </Link>

                {/* Dokümanlar Section */}
                <div className="border-t border-gray-200 pt-4 mt-2">
                  <div className="flex items-center gap-2 text-gray-500 font-semibold text-sm mb-3">
                    <FileText className="w-4 h-4" />
                    {t('howItWorks.nav.docs')}
                  </div>
                  <div className="pl-6 space-y-3">
                    <Link to="/how-it-works" className="flex items-start gap-2 text-gray-700 hover:text-blue-600 font-medium transition">
                      <Zap className="w-4 h-4 mt-0.5" />
                      <div>
                        <div>{t('howItWorks.nav.howItWorks.title')}</div>
                        <div className="text-xs text-gray-500">{t('howItWorks.nav.mobileSubtitle')}</div>
                      </div>
                    </Link>
                    <Link to="/platform-requirements" className="flex items-start gap-2 text-gray-700 hover:text-blue-600 font-medium transition">
                      <Shield className="w-4 h-4 mt-0.5" />
                      <div>
                        <div>{t('howItWorks.nav.platformRequirements.title')}</div>
                        <div className="text-xs text-gray-500">{t('howItWorks.nav.mobileRequirements')}</div>
                      </div>
                    </Link>
                  </div>
                </div>

                <LanguageSwitcher variant="compact" />

                <Link to="/login" className="text-gray-700 hover:text-blue-600 font-medium transition pt-2 border-t border-gray-200">
                  {t('nav.login')}
                </Link>
                <Link
                  to="/register"
                  className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-2 rounded-lg font-semibold text-center"
                >
                  {t('nav.register')}
                </Link>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-12">
        <div className="text-center mb-12">
          <h1 className="text-5xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-4">
            {t('platformRequirements.hero.title')}
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {t('platformRequirements.hero.subtitle')}
          </p>
        </div>

        {/* Important Notice */}
        <div className="bg-gradient-to-r from-orange-500 to-red-500 rounded-2xl p-6 mb-12 text-white shadow-xl">
          <div className="flex items-start gap-4">
            <Info className="w-8 h-8 flex-shrink-0 mt-1" />
            <div>
              <h3 className="text-2xl font-bold mb-2">⚠️ {t('platformRequirements.importantNotice.title')}</h3>
              <p className="text-lg opacity-95 mb-2">
                {t('platformRequirements.importantNotice.description')}
              </p>
              <p className="text-sm opacity-90">
                💡 <strong>{t('platformRequirements.importantNotice.tip')}</strong> {t('platformRequirements.importantNotice.tipContent')}
              </p>
            </div>
          </div>
        </div>

        {/* Platforms List */}
        <div className="space-y-4">
          {platforms.map((platform) => (
            <div
              key={platform.id}
              className="bg-white rounded-2xl shadow-lg overflow-hidden transition-all hover:shadow-xl"
            >
              {/* Platform Header - Clickable */}
              <button
                onClick={() => togglePlatform(platform.id)}
                className="w-full p-6 flex items-center justify-between hover:bg-gray-50 transition"
              >
                <div className="flex items-center gap-4">
                  <div className={`w-16 h-16 bg-gradient-to-r ${platform.color} rounded-xl flex items-center justify-center p-3`}>
                    <img src={platform.logo} alt={platform.name} className="w-full h-full object-contain" />
                  </div>
                  <div className="text-left">
                    <h3 className="text-2xl font-bold text-gray-800">{platform.name}</h3>
                    <p className="text-sm text-gray-500">{platform.apiInfo}</p>
                  </div>
                  {platform.limitedQuota && (
                    <span className="ml-4 bg-orange-100 text-orange-700 px-3 py-1 rounded-full text-xs font-bold">
                      {t('platformRequirements.badges.limitedQuota')}
                    </span>
                  )}
                  {platform.beta && (
                    <span className="ml-4 bg-yellow-100 text-yellow-700 px-3 py-1 rounded-full text-xs font-bold">
                      {t('platformRequirements.badges.beta')}
                    </span>
                  )}
                </div>
                <div className="text-gray-400">
                  {expandedPlatform === platform.id ? '▲' : '▼'}
                </div>
              </button>

              {/* Platform Details - Expandable */}
              {expandedPlatform === platform.id && (
                <div className="border-t-2 border-gray-100 p-6 space-y-6 bg-gray-50">

                  {/* Requirements */}
                  <div>
                    <h4 className="text-lg font-bold text-gray-800 mb-3 flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      {t('platformRequirements.sections.requirements')}
                    </h4>
                    <ul className="space-y-2">
                      {platform.requirements.map((req, index) => (
                        <li
                          key={index}
                          className={`flex items-start gap-3 p-3 rounded-lg ${
                            req.warning ? 'bg-red-50 border-2 border-red-200' : 'bg-white'
                          }`}
                        >
                          {req.required ? (
                            req.warning ? (
                              <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                            ) : (
                              <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                            )
                          ) : (
                            <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                          )}
                          <span className={req.warning ? 'text-red-800 font-semibold' : 'text-gray-700'}>
                            {req.text}
                            {!req.required && <span className="text-xs text-gray-500"> {t('platformRequirements.sections.optional')}</span>}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Steps */}
                  <div>
                    <h4 className="text-lg font-bold text-gray-800 mb-3 flex items-center gap-2">
                      📋 {t('platformRequirements.sections.setup')}
                    </h4>
                    <ol className="space-y-2">
                      {platform.steps.map((step, index) => (
                        <li key={index} className="flex gap-3 items-start bg-white p-3 rounded-lg">
                          <span className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold">
                            {index + 1}
                          </span>
                          <span className="text-gray-700">{step}</span>
                        </li>
                      ))}
                    </ol>
                  </div>

                  {/* Limits */}
                  <div>
                    <h4 className="text-lg font-bold text-gray-800 mb-3 flex items-center gap-2">
                      ⚡ {t('platformRequirements.sections.limits')}
                    </h4>
                    <ul className="space-y-2">
                      {platform.limits.map((limit, index) => (
                        <li key={index} className="flex gap-3 items-start bg-white p-3 rounded-lg">
                          <span className="text-blue-600">•</span>
                          <span className="text-gray-700">{limit}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Warnings */}
                  {platform.warnings && platform.warnings.length > 0 && (
                    <div>
                      <h4 className="text-lg font-bold text-gray-800 mb-3 flex items-center gap-2">
                        <AlertCircle className="w-5 h-5 text-orange-600" />
                        {t('platformRequirements.sections.warnings')}
                      </h4>
                      <div className="space-y-2">
                        {platform.warnings.map((warning, index) => (
                          <div
                            key={index}
                            className={`p-3 rounded-lg border-2 ${
                              warning.includes('🚨')
                                ? 'bg-red-50 border-red-300 text-red-800'
                                : warning.includes('💡')
                                ? 'bg-blue-50 border-blue-300 text-blue-800'
                                : 'bg-orange-50 border-orange-300 text-orange-800'
                            }`}
                          >
                            {warning}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="mt-12 bg-gradient-to-r from-blue-600 to-purple-600 rounded-3xl shadow-2xl p-12 text-center text-white">
          <h2 className="text-4xl font-bold mb-4">{t('platformRequirements.cta.title')}</h2>
          <p className="text-xl mb-8 opacity-90">
            {t('platformRequirements.cta.subtitle')}
          </p>
          <div className="flex gap-4 justify-center flex-wrap">
            <Link
              to="/register"
              className="bg-white text-blue-600 px-8 py-4 rounded-xl font-bold text-lg hover:shadow-2xl transition transform hover:scale-105"
            >
              {t('platformRequirements.cta.freeRegister')}
            </Link>
            <Link
              to="/how-it-works"
              className="bg-blue-700 text-white px-8 py-4 rounded-xl font-bold text-lg hover:bg-blue-800 transition border-2 border-white"
            >
              {t('platformRequirements.cta.viewGuide')}
            </Link>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default PlatformRequirements;
